import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Banknote,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  Filter,
  Heart,
  ListOrdered,
  Pencil,
  Phone,
  PlayCircle,
  Plus,
  Printer,
  Receipt,
  RefreshCw,
  Shield,
  SkipForward,
  Star,
  Stethoscope,
  User,
  Wallet,
  X,
  XCircle,
} from 'lucide-react';
import { visitAPI, doctorAPI, referralAPI, medicalServiceAPI, serverFetch } from '../services/api';
import { useToast } from './UIProvider';
import { useCashDrawer, isDrawerCancelled, refundsInCash } from './cashDrawer/CashDrawerContext';
import useDialog from '../hooks/useDialog';
import DeferVisitModal from './DeferVisitModal';
import EditTokenModal from './EditTokenModal';
import VisitAuditHistory from './VisitAuditHistory';
// The token receipt is shared with Patient Management → Issue Token, so a reprinted slip is
// identical to the one handed over at registration.
import TokenPrintModal, {
  visitIssuedAt as receiptIssuedAt,
  PAYMENT_MODE_LABEL,
  buildTokenPrintData,
  formatVisitToken,
  printTokenReceipt,
} from './TokenPrintModal';
// Shared age-band vitals assessment — the queue chips colour a reading the same way the
// Prescription vitals panel does: neutral when in range, a severity colour only when abnormal
// (age-aware via the patient's DOB, so a child's naturally higher pulse doesn't false-flag).
import { vitalSeverityByLabel, vitalChipToneClass } from '../utils/vitals';
import { formatClinicDate, formatClinicTime, formatClinicDateTime, clinicNow } from '../utils/clinicTime';
import { scrollContentToTop } from '../utils/scroll';
import { toTitleCase } from '../utils/text';
import { formatPatientAge as patientAgeLabel } from '../utils/age';
import { describeGuardian } from '../constants/guardians';
import { accompaniedByOf } from '../utils/guardians';
import Select from './Select';
import { PATIENT_PAYMENT_MODES, PAYMENT_MODE_LABEL as ALL_PAYMENT_MODE_LABELS } from '../constants/payments';
import SortHeader, { useSort, sortRows } from './SortHeader';
import TablePager, { usePagedRows } from './TablePager';
import { onActivateKey, ROW_FOCUS_RING } from '../utils/keyboard';

// One bordered radio row per settlement outcome. Declared out here rather than inside the modal: a
// component declared during render is a new type on every render, so React rebuilt the radio each
// time an outcome was picked and keyboard focus dropped out of the dialog.
const SettleChoice = ({ value, outcome, onPick, icon, title, subtitle, children }) => (
  <label className={`block rounded-xl border-2 p-4 cursor-pointer transition-colors ${
    outcome === value ? 'border-violet-500 bg-violet-50/60' : 'border-gray-200 hover:border-gray-300'
  }`}>
    <div className="flex items-start gap-3">
      <input type="radio" name="settle-outcome" checked={outcome === value} onChange={() => onPick(value)} className="mt-1.5 w-4 h-4 text-violet-600" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 font-semibold text-gray-900">{icon}{title}</div>
        <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
        {outcome === value && children}
      </div>
    </div>
  </label>
);

// ==================== SETTLE REFERRAL MODAL ====================
// Reception's half of an in-clinic referral. The doctor already marked the visit REFERRED;
// this settles the fee the patient paid at the token counter, with exactly three outcomes:
// a token to the new doctor now (fee carries over), hold as patient credit, or cash refund.
// Zero-paid visits collapse to "token now" / "patient will return".
const SettleReferralModal = ({ visit, onClose, onSettled }) => {
  const notify = useToast();
  const { withDrawer } = useCashDrawer();
  const paid = Number(visit.finalFee || 0);
  const toDoctor = visit.referredToDoctor;
  const [outcome, setOutcome] = useState('TOKEN_NOW');
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState('');
  const [excessAction, setExcessAction] = useState('CASH');
  const [topUpMode, setTopUpMode] = useState('CASH');
  const [saving, setSaving] = useState(false);
  const settleDialogRef = useDialog(() => { if (!saving) onClose(); });

  useEffect(() => {
    if (!toDoctor?.id) return;
    medicalServiceAPI.getForDoctor(toDoctor.id)
      .then(list => {
        const arr = Array.isArray(list) ? list : [];
        setServices(arr);
        if (arr.length === 1) setServiceId(arr[0].id);
      })
      .catch(() => setServices([]));
  }, [toDoctor?.id]);

  const service = services.find(s => s.id === serviceId);
  const fee = Number(service?.baseFee || 0);
  const applied = Math.min(paid, fee);
  const topUp = Math.max(0, fee - applied);
  const excess = Math.max(0, paid - applied);
  const rs = (n) => `Rs ${Number(n || 0).toLocaleString()}`;

  const handleConfirm = async () => {
    if (outcome === 'TOKEN_NOW' && !serviceId) {
      notify('warning', 'Pick the service for the new token');
      return;
    }
    setSaving(true);
    try {
      // Cash in (a top-up) or out (a refund, or the excess handed back) goes through the drawer.
      const takesCash = outcome === 'TOKEN_NOW'
        ? (topUp > 0 && topUpMode === 'CASH') || (excess > 0 && excessAction === 'CASH')
        : outcome === 'REFUND' && paid > 0 && refundsInCash(visit.paymentMode);
      const result = await withDrawer(() => referralAPI.settle(visit.id, {
        outcome,
        medicalServiceId: outcome === 'TOKEN_NOW' ? serviceId : null,
        paymentMode: outcome === 'TOKEN_NOW' && topUp > 0 ? topUpMode : null,
        excessAction: outcome === 'TOKEN_NOW' ? excessAction : null,
      }), { cash: takesCash });
      const messages = {
        TOKEN_NOW: `Token ${result.tokenPrefix ? result.tokenPrefix + '-' : ''}${result.tokenNumber} issued to ${toDoctor?.fullName || 'the doctor'}${topUp > 0 ? ` · collect ${rs(topUp)}` : ''}${excess > 0 ? (excessAction === 'CASH' ? ` · return ${rs(excess)} cash` : ` · ${rs(excess)} held as credit`) : ''}`,
        CREDIT: `${rs(paid)} held as credit in the patient's name`,
        REFUND: `${rs(paid)} refunded to the patient`,
        NONE: 'Referral noted. The patient will return later.',
      };
      onSettled(messages[outcome]);
    } catch (e) {
      if (!isDrawerCancelled(e)) notify('error', e.message || 'Failed to settle the referral');
      setSaving(false);
    }
  };

  return (
    <div ref={settleDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Settle Referral</h3>
        <p className="text-sm text-gray-600 mb-4">
          <span className="font-medium">{visit.patient?.firstName} {visit.patient?.lastName}</span> was referred
          {visit.doctor && <> by <span className="font-medium">{visit.doctor.fullName}</span></>}
          {toDoctor && <> to <span className="font-medium">{toDoctor.fullName}</span></>}
          {visit.referralReason && <> · "{visit.referralReason}"</>}
        </p>
        <div className={`rounded-xl px-4 py-3 mb-4 text-sm font-medium ${paid > 0 ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-gray-50 border border-gray-200 text-gray-600'}`}>
          {paid > 0 ? <>Patient paid <span className="font-bold">{rs(paid)}</span> for the original consultation.</> : 'Nothing was paid for the original consultation.'}
        </div>

        <div className="space-y-3">
          <SettleChoice
            value="TOKEN_NOW"
            outcome={outcome}
            onPick={setOutcome}
            icon={<PlayCircle className="w-4 h-4 text-violet-600" />}
            title={`Issue token to ${toDoctor?.fullName || 'the new doctor'} now`}
            subtitle={paid > 0 ? 'The paid fee carries over; only the difference changes hands.' : 'Normal token; the fee is collected as usual.'}
          >
            <div className="mt-3 space-y-3">
              <Select
                value={serviceId}
                onChange={setServiceId}
                placeholder={services.length === 0 ? 'No services for this doctor' : 'Select service'}
                options={services.map(sv => ({
                  value: sv.id,
                  label: sv.name + ' - Rs ' + sv.baseFee + (sv.doctor ? ' (doctor-specific)' : ''),
                }))}
                buttonClassName="px-3 py-2 rounded-lg"
              />
              {service && (
                <div className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">New consultation fee</span><span className="font-semibold">{rs(fee)}</span></div>
                  {paid > 0 && <div className="flex justify-between"><span className="text-gray-500">Carried over</span><span className="font-semibold text-emerald-700">- {rs(applied)}</span></div>}
                  {topUp > 0 && <div className="flex justify-between border-t border-gray-200 pt-1"><span className="text-gray-700 font-medium">Collect from patient</span><span className="font-bold">{rs(topUp)}</span></div>}
                  {topUp === 0 && excess === 0 && paid > 0 && <div className="flex justify-between border-t border-gray-200 pt-1"><span className="text-gray-700 font-medium">Nothing to collect</span><span className="font-bold">Rs 0</span></div>}
                  {excess > 0 && <div className="flex justify-between border-t border-gray-200 pt-1"><span className="text-gray-700 font-medium">Return to patient</span><span className="font-bold text-emerald-700">{rs(excess)}</span></div>}
                </div>
              )}
              {topUp > 0 && (
                <Select
                  value={topUpMode}
                  onChange={setTopUpMode}
                  /* The shared patient list, not a fourth hand-written copy. This one still
                     offered Online, retired from every other picker, and had never been given
                     Bank Transfer or the wallets. */
                  options={PATIENT_PAYMENT_MODES}
                  buttonClassName="px-3 py-2 rounded-lg"
                />
              )}
              {excess > 0 && (
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-1.5"><input type="radio" checked={excessAction === 'CASH'} onChange={() => setExcessAction('CASH')} className="w-4 h-4 text-violet-600" />Return {rs(excess)} in cash</label>
                  <label className="flex items-center gap-1.5"><input type="radio" checked={excessAction === 'CREDIT'} onChange={() => setExcessAction('CREDIT')} className="w-4 h-4 text-violet-600" />Keep as credit</label>
                </div>
              )}
            </div>
          </SettleChoice>

          {paid > 0 ? (
            <>
              <SettleChoice
                value="CREDIT"
                outcome={outcome}
                onPick={setOutcome}
                icon={<Wallet className="w-4 h-4 text-violet-600" />}
                title="Hold as credit"
                subtitle={`${rs(paid)} stays in the patient's name and applies automatically to a future token.`}
              />
              <SettleChoice
                value="REFUND"
                outcome={outcome}
                onPick={setOutcome}
                icon={<Banknote className="w-4 h-4 text-violet-600" />}
                title="Refund in cash"
                subtitle={`Hand ${rs(paid)} back now. The token receipt is marked refunded.`}
              />
            </>
          ) : (
            <SettleChoice
              value="NONE"
              outcome={outcome}
              onPick={setOutcome}
              icon={<Clock className="w-4 h-4 text-violet-600" />}
              title="Patient will return later"
              subtitle="No fee to settle. A token can be issued whenever they come back."
            />
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
          >
            {saving ? 'Settling…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Statuses whose token slip is still worth reprinting: the patient is waiting for vitals, waiting
// for the doctor, or has just been called to the room. Once the doctor has actually started
// (IN_CONSULTATION) — or the visit is COMPLETED / CANCELLED / REFERRED — the slip has served its
// purpose and a duplicate would only confuse the queue.
const REPRINTABLE_STATUSES = ['PENDING_VITALS', 'READY_FOR_DOCTOR', 'CALLED'];

// A token can be corrected right up until the doctor calls the patient in, and no further.
// Everything before that is still reception's to fix. Vitals do NOT close the window: they are
// measurements of the patient rather than of the doctor, so they survive a correction and a
// patient waiting with their BP already taken can still be moved to the right doctor.
// Mirrors VisitService.isEditable on the backend, which is the authority — this copy exists so
// the button doesn't offer an action that would be refused.
// READY_FOR_DOCTOR counts as well as PENDING_VITALS because emergency tokens, and every token in a
// clinic running with Nurse Operational off, start life there.
const isVisitEditable = (visit) =>
  !!visit
  && ['PENDING_VITALS', 'READY_FOR_DOCTOR'].includes(visit.status)
  && !visit.doctorCalledAt;


// What the queue is ORDERED on, which is not always what the row prints. Normally the token's
// issue time; for a deferred patient it is the moment they were deferred, because moving them
// down the queue is the entire point of pressing Defer. Same COALESCE(deferredAt, createdAt)
// key the nurse station queue has always used, so both queues now agree on what a defer means.
const queueSortKey = (v) => (v && v.deferredAt) || receiptIssuedAt(v);

// YYYY-MM-DD formatter that respects local timezone (toISOString would shift by UTC offset).
const toIsoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Render a visit's token with its per-doctor prefix, e.g. "AB-1". Delegates to the shared
// formatter so the queue badge, the receipts table and the printed slip can never disagree.
// The '?' fallback is queue-specific: a row with no token number should show a visible
// placeholder rather than an empty cell.
const formatTokenDisplay = (visit) => formatVisitToken(visit, { fallback: '?' });

// Date-range presets for the OPD Receipts filter bar.
const RECEIPT_DATE_PRESETS = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7',     label: 'Last 7 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'all',       label: 'All Time' },
];

const getReceiptDateRange = (preset) => {
  // Anchor "today" to the clinic's timezone, not the browser's. The queue/stats and these
  // receipts both filter by the backend's clinic-zone calendar day; using the browser's local
  // day here made the two disagree whenever the viewer's timezone straddled the clinic midnight.
  const today = clinicNow();
  today.setHours(0, 0, 0, 0);
  switch (preset) {
    case 'today':
      return { from: toIsoDate(today), to: toIsoDate(today) };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: toIsoDate(y), to: toIsoDate(y) };
    }
    case 'last7': {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from: toIsoDate(from), to: toIsoDate(today) };
    }
    case 'thisMonth': {
      const from = new Date(today);
      from.setDate(1);
      return { from: toIsoDate(from), to: toIsoDate(today) };
    }
    case 'all':
      // "All Time" — go back far enough to cover any realistic clinic history.
      return { from: '2020-01-01', to: toIsoDate(today) };
    default:
      return { from: toIsoDate(today), to: toIsoDate(today) };
  }
};

const TokenManagement = ({ authUser, onNavigateToPatients, hasPermission }) => {
  const { withDrawer } = useCashDrawer();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('QUEUE_MANAGEMENT', action) : true),
    [hasPermission],
  );
  const isDoctor = authUser?.role === 'DOCTOR';

  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('active'); // 'active' | 'today_history' | <specific status>
  // 'active' = PENDING_VITALS + READY_FOR_DOCTOR + CALLED + IN_CONSULTATION (still in the flow)
  // 'today_history' = COMPLETED + CANCELLED for today (already moved past the queue)
  const [filterDoctor, setFilterDoctor] = useState(isDoctor ? authUser.id : 'all');
  // On by default: an emergency is the one case where waiting order is the wrong answer, so the
  // queue should already be showing it at the top when someone opens the screen rather than
  // relying on them to ask for it. Turned off to read the queue in pure arrival order, where an
  // emergency sits in its real position and its wait time can be judged.
  const [emergencyFirst, setEmergencyFirst] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState(null);
  // Keys mirror exactly what loadVisitsAndStats writes below (which mirrors the backend's
  // /visits/stats payload). The old `waiting` / `inProgress` keys were never populated after the
  // first load, so anything reading them silently got undefined.
  const [queueStats, setQueueStats] = useState({ total: 0, pendingVitals: 0, readyForDoctor: 0, called: 0, inConsultation: 0, completed: 0, cancelled: 0, referred: 0, referredPending: 0, emergency: 0, lastTokenNumber: 0 });
  const [cancelModal, setCancelModal] = useState({ show: false, visit: null, reason: '', feeOutcome: '' }); // Cancel modal state
  const [deferVisitTarget, setDeferVisitTarget] = useState(null); // visit currently being deferred
  const [settleVisitTarget, setSettleVisitTarget] = useState(null); // referred visit being settled
  // Reprint — a duplicate token slip for a patient who lost or never received theirs. Held as
  // built receipt data (not the raw visit) so the modal renders exactly what Issue Token renders.
  const [reprintData, setReprintData] = useState(null);
  const reprintRef = useRef(null);
  const [editVisitTarget, setEditVisitTarget] = useState(null); // token being corrected
  const visitDetailDialogRef = useDialog(() => setSelectedVisit(null), { confirmDiscard: false });

  // Page-level view toggle. 'queue' is the existing token-queue table; 'receipts' is the
  // new OPD Receipts tab. OPD Receipts owns its own date-range fetch + filters so it's not
  // bound to the queue's status/doctor/date filter — receptionist reconciling cash for
  // "Yesterday" or "This Month" doesn't want to be coupled to the queue's filter state.
  const [view, setView] = useState('queue');

  // Show toast notification — delegates to the shared UIProvider toast. We keep this local
  // wrapper so existing (message, type) call sites stay unchanged; the hook is (type, message).
  const notify = useToast();
  const showToast = (message, type = 'success') => notify(type, message);

  // Swapping the primary page view (queue <-> receipts) replaces all content inside the app
  // <main>; reset scroll so the new view starts at the top instead of inheriting the prior
  // view's scroll position.
  useEffect(() => { scrollContentToTop(); }, [view]);

  useEffect(() => { if (!isDoctor) loadDoctors(); loadVisitsAndStats(); }, []);
  useEffect(() => { loadVisitsAndStats(); }, [filterStatus, filterDoctor]);

  const loadDoctors = async () => {
    try { 
      const data = await doctorAPI.getAll();
      setDoctors(data || []); 
    } catch (e) { 
      console.error('Error loading doctors:', e);
      setDoctors([]); 
    }
  };

  // How many of the visible visits are emergencies. Drives the toggle, which is only offered
  // when there is actually something to prioritise.
  const emergencyCount = useMemo(() => visits.filter(v => v.isEmergency).length, [visits]);

  // THE QUEUE ORDER.
  //
  // Issue time, across every doctor. Token numbers cannot carry this: they are a per-doctor,
  // per-day sequence (TokenService.generateNextTokenNumber restarts at 1 for each doctor), so
  // ordering by number interleaved the doctors - every AB-1, CD-1, EF-1 together, then every #2.
  // createdAt is the moment the token was issued and is the same clock for all of them.
  //
  // Two things are allowed above that order, in this precedence:
  //   1. An unsettled referral. That is an action reception still owes the patient, not a place
  //      in the queue, so it stays an attention band at the top.
  //   2. Emergencies, but only while the toggle is on. Off, they sit in real arrival position
  //      and are found by their red band instead.
  const orderedVisits = useMemo(() => {
    const band = (v) => {
      if (v.status === 'REFERRED' && v.referralSettlement === 'PENDING') return 0;
      if (emergencyFirst && v.isEmergency) return 1;
      return 2;
    };
    return [...visits].sort((a, b) => {
      const ba = band(a);
      const bb = band(b);
      if (ba !== bb) return ba - bb;
      // Issue time, or the defer time for a patient who was deferred. Both are shown on the
      // row, so whichever one decided the position is visible there.
      const ta = queueSortKey(a);
      const tb = queueSortKey(b);
      // A visit with no timestamp cannot claim a position, so it waits at the back rather than
      // jumping the queue on a blank.
      if (!ta || !tb) return !ta && !tb ? 0 : ta ? -1 : 1;
      // ISO strings, so a plain comparison is already chronological - no Date parsing, and none
      // of the day-shifting that parsing a date-only value invites.
      if (ta !== tb) return ta < tb ? -1 : 1;
      // Two tokens issued in the same second still need one settled answer, or the list can
      // reshuffle between refreshes.
      return (a.tokenNumber || 0) - (b.tokenNumber || 0);
    });
  }, [visits, emergencyFirst]);

  const loadVisitsAndStats = async () => {
    setLoading(true); 
    setError(null);
    try {
      const filters = {};
      
      // 'active' and 'today_history' are client-side multi-status filters; for those we
      // fetch all of today's visits and filter below. Specific statuses go straight to the API.
      if (filterStatus !== 'all' && filterStatus !== 'active' && filterStatus !== 'today_history') {
        filters.status = filterStatus;
      }
      if (filterDoctor !== 'all') filters.doctorId = filterDoctor;
      
      // Stats must be scoped exactly like the list, or the cards contradict the rows beneath them.
      // A logged-in doctor only ever sees their own visits, so clinic-wide cards above that list
      // claimed a total the list could not account for.
      const [visitsData, statsData] = await Promise.all([
        visitAPI.getAll(filters),
        visitAPI.getStats(filterDoctor !== 'all' ? filterDoctor : null)
      ]);
      
      console.log('Visits loaded:', visitsData);
      console.log('Stats loaded:', statsData);
      
      // Active = the entire still-in-progress lifecycle: waiting for vitals, ready for the
      // doctor, or in consultation. The visit only drops off the active queue when it's
      // completed or cancelled — Today's History tracks those.
      let filteredVisits = visitsData || [];
      if (filterStatus === 'active') {
        // Referred-but-unsettled visits stay visible here — reception still owes the patient
        // a settlement (token / credit / refund) even though the visit itself is terminal.
        filteredVisits = filteredVisits.filter(v =>
          v.status === 'PENDING_VITALS' ||
          v.status === 'READY_FOR_DOCTOR' ||
          v.status === 'CALLED' ||
          v.status === 'IN_CONSULTATION' ||
          (v.status === 'REFERRED' && v.referralSettlement === 'PENDING')
        );
      } else if (filterStatus === 'today_history') {
        filteredVisits = filteredVisits.filter(v =>
          v.status === 'COMPLETED' ||
          v.status === 'CANCELLED' ||
          v.status === 'REFERRED'
        );
      }
      setVisits(filteredVisits);
      setQueueStats({
        total: statsData?.total || 0,
        pendingVitals: statsData?.pendingVitals || 0,
        readyForDoctor: statsData?.readyForDoctor || 0,
        called: statsData?.called || 0,
        inConsultation: statsData?.inConsultation || 0,
        completed: statsData?.completed || 0,
        // Previously dropped on the floor, which is why the tab badges disagreed with their own
        // lists: History counted only completed, and Active ignored unsettled referrals entirely.
        cancelled: statsData?.cancelled || 0,
        referred: statsData?.referred || 0,
        referredPending: statsData?.referredPending || 0,
        emergency: statsData?.emergency || 0,
        lastTokenNumber: statsData?.lastTokenNumber || 0
      });
    } catch (err) { 
      console.error('Error loading visits:', err);
      setError('Failed to load visits.'); 
      setVisits([]); 
    } finally { 
      setLoading(false); 
    }
  };

  // Reprint the token slip. Gated on both the visit's stage (see REPRINTABLE_STATUSES) and the
  // Queue Management → Reprint token permission.
  // The two tabs are separately grantable. Hiding a tab is not enough on its own — the view
  // state could still point at it (a saved default, or a permission revoked mid-session) — so the
  // content below is gated too and the effect below lands the user on a tab they can open.
  const visibleTabs = useMemo(() => ([
    { key: 'queue',    label: 'Token Queue',    icon: ListOrdered, perm: 'VIEW_TOKEN_QUEUE_TAB' },
    { key: 'receipts', label: 'Token Receipts', icon: Receipt,     perm: 'VIEW_TOKEN_RECEIPTS_TAB' },
  ].filter(t => can(t.perm))), [can]);

  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (!visibleTabs.some(t => t.key === view)) setView(visibleTabs[0].key);
  }, [visibleTabs, view]);

  const canReprint = (visit) => REPRINTABLE_STATUSES.includes(visit?.status) && can('REPRINT_TOKEN');

  // Editing reuses the module's generic EDIT permission (already granted to Admin, Doctor and
  // Receptionist) rather than introducing a token-specific one.
  const canEdit = (visit) => isVisitEditable(visit) && can('EDIT');

  const openReprint = (visit) => {
    // Same definition the backend stamps on the original slip at issuance
    // (VisitController: pendingVitals + readyForDoctor), so the reprinted count is comparable
    // rather than a different measure of "waiting". It reflects the queue right now, not at
    // issuance time, which is what a patient asking for a duplicate actually wants to know.
    const waitingCount = (queueStats.pendingVitals || 0) + (queueStats.readyForDoctor || 0);
    setReprintData(buildTokenPrintData(visit, { waitingCount }));
  };

  const handleReprint = () => {
    if (!printTokenReceipt(reprintRef.current)) {
      showToast('Could not open the print window. Please allow pop-ups for this site.', 'error');
      return;
    }
    setReprintData(null);
  };

  // Call token - this broadcasts to Display Board via WebSocket
  const handleCallToken = async (id) => {
    try { 
      // Use the new token-calls API that broadcasts to Display Board
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      const response = await serverFetch(`${apiBase}/token-calls/${id}/call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('clinic_auth_token') ? { 'Authorization': `Bearer ${localStorage.getItem('clinic_auth_token')}` } : {}),
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to call token');
      }
      
      const data = await response.json();
      console.log('Token called:', data);
      
      // Show success toast
      showToast(`Token ${data.tokenNumber} called for ${data.patientName}`, 'success');
      
      loadVisitsAndStats(); 
    } catch (e) { 
      console.error('Error calling token:', e);
      showToast('Failed to call token: ' + e.message, 'error'); 
    } 
  };

  // Recall token (call again)
  const handleRecallToken = async (id) => { 
    try { 
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      const response = await serverFetch(`${apiBase}/token-calls/${id}/recall`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('clinic_auth_token') ? { 'Authorization': `Bearer ${localStorage.getItem('clinic_auth_token')}` } : {}),
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to recall token');
      }
      
      showToast('Token recalled! Announcement sent to Display Board', 'success');
    } catch (e) { 
      console.error('Error recalling token:', e);
      showToast('Failed to recall token: ' + e.message, 'error'); 
    } 
  };
  
  const handleDeferVisit = async (afterVisitId) => {
    const visitId = deferVisitTarget?.id;
    if (!visitId) return;
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      const response = await serverFetch(`${apiBase}/visits/${visitId}/defer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('clinic_auth_token') ? { 'Authorization': `Bearer ${localStorage.getItem('clinic_auth_token')}` } : {}),
        },
        body: JSON.stringify({ afterVisitId: afterVisitId || null }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to defer patient');
      }
      showToast('Patient deferred - re-queued for callback', 'success');
      setDeferVisitTarget(null);
      loadVisitsAndStats();
    } catch (e) {
      showToast('Failed to defer patient: ' + e.message, 'error');
    }
  };

  const handleCompleteVisit = async (id) => {
    try { 
      await visitAPI.completeVisit(id); 
      showToast('Visit completed successfully', 'success');
      loadVisitsAndStats(); 
    } catch (e) { 
      showToast('Failed: ' + e.message, 'error'); 
    } 
  };
  
  // Open cancel modal. The whole visit is kept, not just the id: cancelling has to settle
  // whatever fee and patient credit are attached, and the modal has to show what those are.
  const openCancelModal = (visit) => {
    setCancelModal({ show: true, visit, reason: '', feeOutcome: '' });
  };
  const closeCancelModal = () => setCancelModal({ show: false, visit: null, reason: '', feeOutcome: '' });
  const cancelDialogRef = useDialog(closeCancelModal);

  // Handle cancel confirmation
  const handleCancelVisit = async () => { 
    if (!cancelModal.reason.trim()) {
      showToast('Please provide a cancellation reason', 'error');
      return;
    }
    const paid = Number(cancelModal.visit?.finalFee || 0);
    if (paid > 0 && !cancelModal.feeOutcome) {
      showToast('Say what happens to the fee that was paid', 'error');
      return;
    }
    try { 
      // A cash refund comes out of the cashier's drawer, which is opened first if it is not already.
      const cashBack = paid > 0 && cancelModal.feeOutcome === 'REFUND' && refundsInCash(cancelModal.visit?.paymentMode);
      await withDrawer(
        () => visitAPI.cancelVisit(cancelModal.visit.id, cancelModal.reason, cancelModal.feeOutcome || null),
        { cash: cashBack },
      );
      // The toast names where the money went, because that is the part of a cancellation
      // nobody can check afterwards without going digging.
      const credit = Number(cancelModal.visit?.appliedCreditAmount || 0);
      const money = [
        paid > 0 && cancelModal.feeOutcome === 'REFUND' ? `Rs ${paid.toLocaleString()} refunded` : null,
        paid > 0 && cancelModal.feeOutcome === 'CREDIT' ? `Rs ${paid.toLocaleString()} held as credit` : null,
        paid > 0 && cancelModal.feeOutcome === 'KEEP' ? `Rs ${paid.toLocaleString()} retained` : null,
        credit > 0 ? `Rs ${credit.toLocaleString()} credit returned` : null,
      ].filter(Boolean).join(', ');
      showToast(money ? `Visit cancelled. ${money}.` : 'Visit cancelled successfully', 'success');
      closeCancelModal();
      loadVisitsAndStats(); 
    } catch (e) { 
      if (!isDrawerCancelled(e)) showToast('Failed to cancel visit: ' + e.message, 'error'); 
    } 
  };

  const getStatusColor = (s) => ({
    PENDING_VITALS: 'from-purple-500 to-pink-600',
    READY_FOR_DOCTOR: 'from-yellow-500 to-amber-600',
    CALLED: 'from-orange-500 to-amber-600',
    IN_CONSULTATION: 'from-blue-500 to-indigo-600',
    REFERRED: 'from-violet-500 to-purple-600',
    COMPLETED: 'from-emerald-500 to-teal-600',
    CANCELLED: 'from-gray-400 to-gray-500'
  }[s] || 'from-gray-400 to-gray-500');

  const getStatusBadgeColor = (s) => ({
    PENDING_VITALS: 'bg-purple-100 text-purple-800',
    READY_FOR_DOCTOR: 'bg-yellow-100 text-yellow-800',
    CALLED: 'bg-orange-100 text-orange-800',
    IN_CONSULTATION: 'bg-blue-100 text-blue-800',
    REFERRED: 'bg-violet-100 text-violet-800',
    COMPLETED: 'bg-emerald-100 text-emerald-800',
    CANCELLED: 'bg-gray-100 text-gray-800'
  }[s] || 'bg-gray-100 text-gray-800');

  const getStatusLabel = (s) => ({
    PENDING_VITALS: 'Pending Vitals',
    READY_FOR_DOCTOR: 'Ready for Doctor',
    CALLED: 'Called',
    IN_CONSULTATION: 'In Consultation',
    REFERRED: 'Referred',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled'
  }[s] || s);

  // Token display including the doctor's prefix (e.g. "AB-1"). See formatTokenDisplay above.
  const getTokenDisplay = (visit) => formatTokenDisplay(visit);

  // Helper to safely format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₨0';
    return `₨${Number(amount).toLocaleString()}`;
  };

  // Normalise stored CNIC (with or without dashes) to XXXXX-XXXXXXX-X.
  const formatCnic = (cnic) => {
    if (!cnic) return null;
    const clean = String(cnic).replace(/-/g, '');
    return clean.length === 13
      ? `${clean.slice(0, 5)}-${clean.slice(5, 12)}-${clean.slice(12)}`
      : cnic;
  };

  // Render the human age from dateOfBirth, "about 6 years" when the date is an estimate. Falls
  // back to a stored `age` field if DOB is missing (legacy data).
  const formatPatientAge = (patient) => {
    if (!patient) return null;
    if (patient.dateOfBirth) return patientAgeLabel(patient);
    return patient.age ? `${patient.age} years` : null;
  };

  // Pick the patient's primary relation label using the same priority as elsewhere in
  // the app: minor → Guardian; adult woman married/widowed with husband on file → Husband;
  // otherwise → Father. Returns null if none apply.
  const pickRelation = (patient) => {
    if (!patient) return null;
    const isMinor = patient.adult === false || patient.minor === true;
    if (isMinor && patient.guardianName) return { label: 'Guardian', value: patient.guardianName };
    if (patient.husbandName) return { label: 'Husband', value: patient.husbandName };
    if (patient.fatherName) return { label: 'Father', value: patient.fatherName };
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
              <Calendar className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Token Management</h1>
              <p className="text-gray-600">Today's visits and queue</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Issue Token — routes to Patient Management, where the patient-search +
                IssueTokenModal flow already lives. Hidden if the host App didn't pass
                the navigation callback (e.g. embedded usage). */}
            {onNavigateToPatients && (
              <button
                onClick={onNavigateToPatients}
                className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                title="Search a patient and issue a new token"
              >
                <Plus className="h-5 w-5" />
                <span>Issue Token</span>
              </button>
            )}
            <button onClick={loadVisitsAndStats} disabled={loading} className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* View tabs — Queue vs OPD Receipts. Receipts is the mini-invoice trail for the
            consultation fee captured at token-issuance time (different from the Invoice
            module, which covers downstream services after the doctor sees the patient). */}
        <div className="flex gap-2 mb-4">
          {visibleTabs.map(t => {
            const Icon = t.icon;
            const isActive = view === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200/60'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {view === 'receipts' && can('VIEW_TOKEN_RECEIPTS_TAB') && <OpdReceiptsView />}

        {view === 'queue' && can('VIEW_TOKEN_QUEUE_TAB') && (
        <>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
          {[
            { label: 'Total', value: queueStats.total, color: 'bg-blue-500', icon: <Calendar className="h-4 w-4" /> },
            { label: 'Pending Vitals', value: queueStats.pendingVitals, color: 'bg-purple-500', icon: <Heart className="h-4 w-4" /> },
            { label: 'Ready', value: queueStats.readyForDoctor, color: 'bg-yellow-500', icon: <Clock className="h-4 w-4" /> },
            { label: 'Called', value: queueStats.called || 0, color: 'bg-orange-500', icon: <PlayCircle className="h-4 w-4" /> },
            { label: 'In Consultation', value: queueStats.inConsultation, color: 'bg-indigo-500', icon: <Activity className="h-4 w-4" /> },
            { label: 'Completed', value: queueStats.completed, color: 'bg-emerald-500', icon: <CheckCircle className="h-4 w-4" /> },
            // Cancelled visits count toward Total, so without this card the cards never added up.
            { label: 'Cancelled', value: queueStats.cancelled || 0, color: 'bg-gray-500', icon: <XCircle className="h-4 w-4" /> },
            { label: 'Emergency', value: queueStats.emergency, color: 'bg-red-500', icon: <AlertTriangle className="h-4 w-4" /> },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl shadow-md p-4">
              <div className={`w-8 h-8 ${s.color} rounded-lg flex items-center justify-center text-white mb-2`}>
                {s.icon}
              </div>
              <div className="text-2xl font-bold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tab bar — primary toggle between the still-waiting queue and today's processed list.
            Mirrors the Nurse Station's two-tab pattern so the navigation feels consistent. */}
        <div className="flex gap-2 mb-4">
          {[
            // Each badge counts exactly what its own list filter shows, or the number contradicts
            // the rows underneath it. Active includes referred-but-unsettled visits because the
            // list keeps them; History includes cancelled and referred for the same reason.
            { key: 'active',         label: 'Active Queue',    count: queueStats.pendingVitals + queueStats.readyForDoctor + (queueStats.called || 0) + queueStats.inConsultation + (queueStats.referredPending || 0) },
            { key: 'today_history',  label: "Today's History", count: (queueStats.completed || 0) + (queueStats.cancelled || 0) + (queueStats.referred || 0) },
          ].map(t => {
            const isActive = filterStatus === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setFilterStatus(t.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200/60'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                }`}
              >
                <span>{t.label}</span>
                {t.count > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    isActive ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filters — sub-status drilldown (e.g. only Completed within History) plus doctor filter. */}
        <div className="bg-white rounded-2xl shadow-xl p-4 mb-6 flex flex-wrap items-center gap-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-700">Filters:</span>
          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: 'active', label: 'Active Queue (Pending Vitals + Ready + Called + In Consultation)' },
              { value: 'today_history', label: "Today's History (Completed + Cancelled + Referred)" },
              { value: 'all', label: 'All Status' },
              { value: 'PENDING_VITALS', label: 'Pending Vitals' },
              { value: 'READY_FOR_DOCTOR', label: 'Ready for Doctor' },
              { value: 'CALLED', label: 'Called' },
              { value: 'IN_CONSULTATION', label: 'In Consultation' },
              { value: 'REFERRED', label: 'Referred' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'CANCELLED', label: 'Cancelled' },
            ]}
            className="min-w-[20rem]"
            buttonClassName="px-4 py-2 rounded-lg"
          />
          {isDoctor ? (
            <div className="flex items-center space-x-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
              <Stethoscope className="w-4 h-4 text-emerald-600" />
              <span className="font-medium text-emerald-800">{authUser.fullName}</span>
            </div>
          ) : (
            <Select
              value={filterDoctor}
              onChange={setFilterDoctor}
              options={[{ value: 'all', label: 'All Doctors' }, ...doctors.map((d) => ({ value: d.id, label: d.fullName }))]}
              className="min-w-[12rem]"
              buttonClassName="px-4 py-2 rounded-lg"
            />
          )}
          {/* Only offered when there is an emergency to prioritise, so the control never promises
              a reordering that would do nothing. */}
          {emergencyCount > 0 && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
              emergencyFirst ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
            }`}>
              <button
                type="button"
                role="switch"
                aria-checked={emergencyFirst}
                onClick={() => setEmergencyFirst(v => !v)}
                aria-label={emergencyFirst
                  ? 'Emergencies are pinned above the queue. Turn off for arrival order.'
                  : 'Queue is in arrival order. Turn on to pull emergencies to the top.'}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 cursor-pointer ${
                  emergencyFirst ? 'bg-red-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  emergencyFirst ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
              {/* The label is clickable too: it is the bigger target of the two. */}
              <button
                type="button"
                onClick={() => setEmergencyFirst(v => !v)}
                className={`flex items-center gap-1.5 text-sm font-semibold cursor-pointer ${
                  emergencyFirst ? 'text-red-700' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <AlertTriangle className="h-4 w-4" />
                Emergency first
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  emergencyFirst ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  {emergencyCount}
                </span>
              </button>
            </div>
          )}
          <div className="ml-auto text-sm text-gray-500">
            Showing {visits.length} visits
            <span className="text-gray-400"> · {emergencyFirst ? 'emergencies first' : 'in arrival order'}</span>
          </div>
        </div>

        {/* Visits List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
          {loading ? (
            // Loading skeleton
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-6 flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-200 rounded-2xl animate-pulse"></div>
                <div className="flex-1">
                  <div className="w-48 h-5 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            ))
          ) : orderedVisits.length > 0 ? (
            orderedVisits.map((visit) => (
              <div key={visit.id} className={`p-6 hover:bg-gray-50 transition-colors ${visit.isEmergency ? 'bg-red-50 border-l-4 border-red-500' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Token Number Badge */}
                    <div className={`w-16 h-16 bg-gradient-to-br ${getStatusColor(visit.status)} rounded-2xl flex items-center justify-center shadow-lg`}>
                      <span className="text-2xl font-bold text-white">{getTokenDisplay(visit)}</span>
                    </div>
                    
                    {/* Patient Info */}
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-lg font-bold text-gray-900">
                          {visit.patient?.fullName || visit.patient?.firstName + ' ' + visit.patient?.lastName || 'Unknown Patient'}
                        </h3>
                        {visit.isEmergency && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full flex items-center">
                            <AlertTriangle className="h-3 w-3 mr-1" />EMERGENCY
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex items-center">
                          <User className="h-4 w-4 mr-1 text-gray-400" />
                          {visit.patient?.mrn || 'N/A'}
                        </span>
                        <span className="flex items-center">
                          {visit.patient?.gender ? toTitleCase(visit.patient.gender) : 'N/A'}
                        </span>
                        <span className="flex items-center">
                          <Stethoscope className="h-4 w-4 mr-1 text-gray-400" />
                          {visit.doctor?.fullName || visit.doctor?.firstName + ' ' + visit.doctor?.lastName || 'N/A'}
                        </span>
                        {/* The token's issue time, which is what the queue is ordered by. */}
                        <span className="flex items-center" title="Token issued at">
                          <Clock className="h-4 w-4 mr-1 text-gray-400" />
                          {formatClinicTime(receiptIssuedAt(visit), {}, 'N/A')}
                        </span>
                        {/* A deferred patient sorts by this instead of their issue time. Without
                            it on the row they would simply read as being in the wrong place. */}
                        {visit.deferredAt && (
                          <span
                            className="flex items-center text-amber-600 font-medium"
                            title="Deferred after a no-show, so this patient is queued from this time instead of their issue time"
                          >
                            <SkipForward className="h-4 w-4 mr-1" />
                            Deferred {formatClinicTime(visit.deferredAt, {}, '')}
                          </span>
                        )}
                      </div>
                      {/* Vitals summary — shown once the nurse records them */}
                      {(visit.bpSystolic || visit.pulse || visit.temperature || visit.spo2) && (() => {
                        // Severity per vital against the patient's age band (DOB on the visit's
                        // patient). Chip is neutral when in range, coloured only when abnormal.
                        const vsev = vitalSeverityByLabel(
                          { bpSystolic: visit.bpSystolic, bpDiastolic: visit.bpDiastolic, pulse: visit.pulse, temperature: visit.temperature, spo2: visit.spo2 },
                          visit.patient?.dateOfBirth,
                        );
                        return (
                        <div className="flex items-center gap-3 mt-1.5 text-xs">
                          {visit.bpSystolic && visit.bpDiastolic && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium ${vitalChipToneClass(vsev('BP'))}`}>
                              BP {visit.bpSystolic}/{visit.bpDiastolic}
                            </span>
                          )}
                          {visit.pulse && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium ${vitalChipToneClass(vsev('Pulse'))}`}>
                              Pulse {visit.pulse}
                            </span>
                          )}
                          {visit.temperature && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium ${vitalChipToneClass(vsev('Temp'))}`}>
                              Temp {visit.temperature}°F
                            </span>
                          )}
                          {visit.spo2 && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium ${vitalChipToneClass(vsev('SpO₂'))}`}>
                              SpO₂ {visit.spo2}%
                            </span>
                          )}
                        </div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Right side - Fee, Status, Actions */}
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(visit.finalFee || visit.finalAmount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {visit.medicalService?.name || visit.service?.name || 'N/A'}
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(visit.status)}`}>
                      {getStatusLabel(visit.status) || 'Unknown'}
                    </span>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => setSelectedVisit(visit)} 
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                        title="View Details"
                      >
                        <Eye className="h-5 w-5" />
                      </button>

                      {/* Correct a token issued with the wrong doctor / service / discount, any
                          time before the doctor calls the patient in. See isVisitEditable. */}
                      {canEdit(visit) && (
                        <button
                          onClick={() => setEditVisitTarget(visit)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit token"
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                      )}

                      {/* Reprint a duplicate slip — only while the patient is still waiting
                          (vitals pending / ready / called). See REPRINTABLE_STATUSES. */}
                      {canReprint(visit) && (
                        <button
                          onClick={() => openReprint(visit)}
                          className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Reprint token slip"
                        >
                          <Printer className="h-5 w-5" />
                        </button>
                      )}

                      {visit.status === 'READY_FOR_DOCTOR' && can('CALL_TOKEN') && (
                        <button
                          onClick={() => handleCallToken(visit.id)}
                          className="p-2 text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors flex items-center space-x-1"
                          title="Call Token - Announces on Display Board"
                        >
                          <PlayCircle className="h-5 w-5" />
                          <span className="text-xs font-medium">Call</span>
                        </button>
                      )}

                      {/* Recall/Defer apply while the patient is summoned but hasn't reached the
                          room (CALLED). Once the doctor starts the prescription the visit is
                          IN_CONSULTATION and re-announcing / deferring no longer makes sense. */}
                      {visit.status === 'CALLED' && (
                        <>
                          {can('RECALL_TOKEN') && (
                            <button
                              onClick={() => handleRecallToken(visit.id)}
                              className="p-2 text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors flex items-center space-x-1"
                              title="Recall Token - Announce again"
                            >
                              <RefreshCw className="h-4 w-4" />
                              <span className="text-xs font-medium">Recall</span>
                            </button>
                          )}
                          {can('DEFER_VISIT') && (
                            <button
                              onClick={() => setDeferVisitTarget(visit)}
                              className="p-2 text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors flex items-center space-x-1"
                              title="Defer - move this patient down the queue"
                            >
                              <SkipForward className="h-4 w-4" />
                              <span className="text-xs font-medium">Defer</span>
                            </button>
                          )}
                        </>
                      )}

                      {/* Settle a referred visit's paid fee — token to the new doctor,
                          credit, or refund. */}
                      {visit.status === 'REFERRED' && visit.referralSettlement === 'PENDING' && can('SETTLE_REFERRAL') && (
                        <button
                          onClick={() => setSettleVisitTarget(visit)}
                          className="p-2 text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors flex items-center space-x-1"
                          title="Settle the referral with the patient"
                        >
                          <ArrowRight className="h-4 w-4" />
                          <span className="text-xs font-medium">Settle</span>
                        </button>
                      )}

                      {['PENDING_VITALS', 'READY_FOR_DOCTOR', 'CALLED', 'IN_CONSULTATION'].includes(visit.status) && (
                        <button
                          onClick={() => openCancelModal(visit)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Empty state
            <div className="p-12 text-center">
              <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-500">No Visits Found</h3>
              <p className="text-gray-400 mt-2">No visits match your current filters</p>
            </div>
          )}
        </div>

        {/* Visit Detail Modal */}
        {selectedVisit && (
          <div ref={visitDetailDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-navy-800 to-navy-700 text-white p-8 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                      <span className="text-3xl font-bold">{getTokenDisplay(selectedVisit)}</span>
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold mb-2">
                        {selectedVisit.patient?.fullName || selectedVisit.patient?.firstName + ' ' + selectedVisit.patient?.lastName}
                      </h1>
                      <div className="flex items-center space-x-4 text-white text-opacity-90">
                        <span>{selectedVisit.patient?.mrn}</span>
                        <span>{selectedVisit.visitTime}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    aria-label="Close"
                    onClick={() => setSelectedVisit(null)} 
                    className="p-3 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {/* Modal Body */}
              <div className="p-4 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Patient Info Card */}
                <div className="bg-blue-50 rounded-2xl p-6">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-blue-600" />Patient Information
                  </h3>
                  <div className="space-y-2">
                    {(() => {
                      const p = selectedVisit.patient || {};
                      const relation = pickRelation(p);
                      // Who brought a child in on this visit, who need not be the guardian on file.
                      const broughtBy = accompaniedByOf(selectedVisit);
                      const rows = [
                        ['Name', p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim()],
                        relation && [relation.label, relation.value],
                        broughtBy && ['Brought by', describeGuardian(broughtBy)],
                        ['MRN', p.mrn],
                        ['CNIC', formatCnic(p.cnic)],
                        ['Phone', p.phoneNumber || p.phone || p.primaryPhone],
                        ['Age / Gender',
                          [formatPatientAge(p), p.gender].filter(Boolean).join(' / ') || null],
                      ].filter(Boolean);
                      return rows.map(([label, value], i) => (
                        <div key={i} className="flex justify-between p-2 bg-white rounded-lg">
                          <span className="text-gray-600">{label}</span>
                          <span className="font-bold">{value || 'N/A'}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                
                {/* Visit Info Card */}
                <div className="bg-emerald-50 rounded-2xl p-6">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <Stethoscope className="h-5 w-5 mr-2 text-emerald-600" />Visit Information
                  </h3>
                  <div className="space-y-2">
                    {[
                      ['Doctor', selectedVisit.doctor?.fullName || `${selectedVisit.doctor?.firstName} ${selectedVisit.doctor?.lastName}`],
                      ['Service', selectedVisit.medicalService?.name || selectedVisit.service?.name],
                      ['Original Fee', formatCurrency(selectedVisit.originalFee)],
                      ['Final Fee', formatCurrency(selectedVisit.finalFee || selectedVisit.finalAmount)],
                      ['Status', selectedVisit.status?.replace('_', ' ')]
                    ].map(([label, value], i) => (
                      <div key={i} className="flex justify-between p-2 bg-white rounded-lg">
                        <span className="text-gray-600">{label}</span>
                        <span className="font-bold">{value || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Change history — spans both columns. Answers "has this token been altered
                    since it was issued, and by whom", which matters most for the fee. */}
                <div className="md:col-span-2">
                  <VisitAuditHistory visitId={selectedVisit.id} />
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-8 pb-8 flex justify-end">
                <button
                  onClick={() => setSelectedVisit(null)}
                  className="px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Visit Modal */}
        {cancelModal.show && (
          <div ref={cancelDialogRef} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <X className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Cancel Visit</h3>
                  </div>
                  <button
                    aria-label="Close"
                    onClick={closeCancelModal}
                    className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>
              
              {/* Body */}
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Please provide a reason for cancelling this visit. This will be recorded for future reference.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cancellation Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={cancelModal.reason}
                    onChange={(e) => setCancelModal({ ...cancelModal, reason: e.target.value })}
                    placeholder="Enter reason for cancellation..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                    rows={3}
                    autoFocus
                  />
                </div>

                {/* Money on the token. Cancelling used to leave a paid fee unaccounted for, so
                    the decision is asked for here rather than assumed. */}
                {Number(cancelModal.visit?.finalFee || 0) > 0 && (() => {
                  const paid = Number(cancelModal.visit.finalFee);
                  const opts = [
                    { value: 'REFUND', label: 'Refund in cash',
                      hint: `Hand Rs ${paid.toLocaleString()} back now. The receipt is marked refunded.` },
                    { value: 'CREDIT', label: 'Hold as credit',
                      hint: `Rs ${paid.toLocaleString()} stays in the patient's name and applies to a future token.` },
                    { value: 'KEEP', label: 'Clinic keeps the fee',
                      hint: 'Nothing is handed back. The reason above records why.' },
                  ];
                  return (
                    <div className="mt-5">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rs {paid.toLocaleString()} was paid. What happens to it? <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {opts.map(o => (
                          <label
                            key={o.value}
                            className={`block rounded-xl border-2 p-3 cursor-pointer transition-colors ${
                              cancelModal.feeOutcome === o.value
                                ? 'border-red-500 bg-red-50/60'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="radio"
                                name="cancel-fee-outcome"
                                checked={cancelModal.feeOutcome === o.value}
                                onChange={() => setCancelModal({ ...cancelModal, feeOutcome: o.value })}
                                className="mt-1 w-4 h-4 text-red-600"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-900 text-sm">{o.label}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{o.hint}</div>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Credit the token consumed is always returned, so it is stated rather than
                    offered. It was the patient's balance, never the clinic's to decide on. */}
                {Number(cancelModal.visit?.appliedCreditAmount || 0) > 0 && (
                  <div className="mt-4 flex items-start gap-2 text-sm bg-violet-50 border border-violet-200 rounded-xl px-3 py-2.5">
                    <Wallet className="h-4 w-4 text-violet-600 mt-0.5 flex-shrink-0" />
                    <span className="text-violet-900">
                      Rs {Number(cancelModal.visit.appliedCreditAmount).toLocaleString()} of patient
                      credit was used on this token. It goes back to their balance automatically.
                    </span>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={closeCancelModal}
                  className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Keep Visit
                </button>
                <button
                  onClick={handleCancelVisit}
                  className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all font-medium shadow-lg shadow-red-500/30"
                >
                  Cancel Visit
                </button>
              </div>
            </div>
          </div>
        )}

        {deferVisitTarget && (
          <DeferVisitModal
            visit={deferVisitTarget}
            queue={visits}
            onClose={() => setDeferVisitTarget(null)}
            onConfirm={handleDeferVisit}
          />
        )}

        {settleVisitTarget && (
          <SettleReferralModal
            visit={settleVisitTarget}
            onClose={() => setSettleVisitTarget(null)}
            onSettled={(message) => {
              setSettleVisitTarget(null);
              showToast(message, 'success');
              loadVisitsAndStats();
            }}
          />
        )}

        {editVisitTarget && (
          <EditTokenModal
            visit={editVisitTarget}
            doctors={doctors}
            onClose={() => setEditVisitTarget(null)}
            onSaved={(updated, { doctorChanged }) => {
              setEditVisitTarget(null);
              showToast(
                doctorChanged
                  ? `Token updated. New number ${formatTokenDisplay(updated)} for ${updated.doctor?.fullName || 'the new doctor'}.`
                  : 'Token updated.',
                'success',
              );
              loadVisitsAndStats();
              // Reassigning the doctor renumbers the token, so the slip the patient is holding is
              // now wrong. Go straight to the reprint rather than relying on staff to remember.
              if (doctorChanged) openReprint(updated);
            }}
          />
        )}

        {/* Duplicate token slip — the same receipt component the Issue Token flow uses, so the
            printed paper is identical. isReprint only adjusts this modal's own wording. */}
        <TokenPrintModal
          show={!!reprintData}
          isReprint
          printData={reprintData}
          printRef={reprintRef}
          onPrint={handleReprint}
          onClose={() => setReprintData(null)}
        />
        </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// OPD Receipts tab — mini-invoice trail for the consultation fee captured at
// token-issuance time. Owns its own date-range fetch (independent of the queue
// view) so the receptionist can reconcile cash for arbitrary ranges without
// disturbing the live queue filter state.
// ============================================================================
export const OpdReceiptsView = () => {
  const [datePreset, setDatePreset] = useState('today');
  const [paymentModeFilter, setPaymentModeFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // receipt row opened in the detail modal

  const range = useMemo(() => getReceiptDateRange(datePreset), [datePreset]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await visitAPI.getAll({ fromDate: range.from, toDate: range.to });
      setVisits(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load receipts');
      setVisits([]);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = (visits || []).filter(v => {
      if (paymentModeFilter === 'ALL') {
        // fall through
      } else if (paymentModeFilter === 'UNRECORDED') {
        if (v.paymentMode) return false;
      } else if (v.paymentMode !== paymentModeFilter) {
        return false;
      }
      if (term) {
        const p = v.patient || {};
        const d = v.doctor || {};
        const haystack = [
          p.firstName, p.lastName, p.mrn, p.phone, p.cnic,
          d.firstName, d.lastName, d.fullName,
          v.medicalService?.name,
          v.tokenNumber != null ? String(v.tokenNumber) : '',
          formatTokenDisplay(v), // so "AB-1" / "AB" prefix searches match

        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
    // Newest first by date then token.
    return filtered.slice().sort((a, b) => {
      const da = a.visitDate || '';
      const db = b.visitDate || '';
      if (da !== db) return db.localeCompare(da);
      return (b.tokenNumber || 0) - (a.tokenNumber || 0);
    });
  }, [visits, paymentModeFilter, searchTerm]);

  // Newest-first stands until a column is picked. Fee sorts on the number, not on the
  // "PKR 1,200" the cell prints, and Token on its display form so AB-2 lands before AB-10.
  const { sortConfig, handleSort } = useSort();
  const sortedRows = useMemo(() => sortRows(rows, sortConfig, {
    date:    (v) => v.visitDate,
    token:   (v) => formatTokenDisplay(v),
    patient: (v) => `${v.patient?.firstName || ''} ${v.patient?.lastName || ''}`.trim(),
    doctor:  (v) => v.doctor?.fullName || `${v.doctor?.firstName || ''} ${v.doctor?.lastName || ''}`.trim(),
    service: (v) => v.medicalService?.name,
    fee:     (v) => Number(v.finalFee || 0),
    mode:    (v) => (v.paymentMode ? (PAYMENT_MODE_LABEL[v.paymentMode] || v.paymentMode) : ''),
    status:  (v) => v.status,
  }), [rows, sortConfig]);
  const paging = usePagedRows(sortedRows, {
    id: 'opd-receipts',
    resetOn: [sortConfig, datePreset, paymentModeFilter, searchTerm],
  });

  // Summary totals for the visible rows, written to describe the cash drawer rather than the
  // appointment book.
  //
  // A fee is taken at the desk the moment the token is issued, so it is in the drawer whatever
  // the visit later becomes. Cancelling used to remove the row from this sum entirely, which was
  // a fair guess back when a cancelled token had no way to say what happened to its money. Now it
  // does: refunded, held as credit, or kept. So the fee counts, and a refund is what takes it back
  // out. That makes each outcome land correctly — a refunded cancellation nets to zero, a kept one
  // stays in the drawer where the money actually is, and a credited one likewise (the clinic still
  // holds the cash; the debt to the patient sits on the credit ledger, not in this till).
  //
  // Refunds are counted wherever they happen, cancellations included. Money handed back is money
  // handed back, and skipping the row meant a real refund showed here as Rs 0.
  //
  // Visits still counts only what actually happened, so a cancellation is not a visit.
  const totals = useMemo(() => {
    let count = 0;
    let collected = 0;
    let refunded = 0;
    for (const v of rows) {
      if (v.paymentMode && v.paymentMode !== 'NONE') {
        collected += Number(v.finalFee || 0);
      }
      refunded += Number(v.refundedAmount || 0);
      if (v.status !== 'CANCELLED') count++;
    }
    return { count, collected: collected - refunded, refunded };
  }, [rows]);

  return (
    // No overflow-hidden on this card: the payment-mode dropdown in the filter bar would be
    // clipped by it whenever the results table below is short. Nothing paints into the corners,
    // so the card's own radius still reads correctly.
    <div className="bg-white rounded-2xl shadow-xl">
      {/* Filter bar — date presets, payment mode, free-text search, and a running totals
          summary on the right. */}
      <div className="px-6 py-4 border-b border-gray-100 space-y-3">
        {/* Row 1: date presets + totals */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {RECEIPT_DATE_PRESETS.map(p => {
              const isActive = datePreset === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => setDatePreset(p.key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="text-sm text-gray-700 flex items-center gap-5">
            <span>Visits: <span className="font-semibold text-gray-900">{totals.count}</span></span>
            {totals.refunded > 0 && (
              <span>Refunded: <span className="font-semibold text-rose-600">PKR {totals.refunded.toLocaleString()}</span></span>
            )}
            <span>Collected: <span className="font-semibold text-emerald-700">PKR {totals.collected.toLocaleString()}</span></span>
          </div>
        </div>
        {/* Row 2: payment-mode filter + search */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Payment mode:</label>
            <Select
              value={paymentModeFilter}
              onChange={setPaymentModeFilter}
              /* A filter is the one place retired methods belong: old receipts still carry
                 Online and Other, and they have to stay findable. Built from the shared label map
                 so a method added there needs no second edit here. UNRECORDED is the filter's own
                 value for a visit that never had a method stored; the filtering code below has
                 always handled it, but nothing offered it. */
              options={[
                { value: 'ALL', label: 'All' },
                ...Object.entries(ALL_PAYMENT_MODE_LABELS)
                  .filter(([value]) => value !== 'NONE')
                  .map(([value, label]) => ({ value, label })),
                { value: 'NONE', label: 'No charge' },
                { value: 'UNRECORDED', label: 'Not recorded' },
              ]}
              className="min-w-[9rem]"
              buttonClassName="px-3 py-2 rounded-lg"
            />
          </div>
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by patient name, MRN, doctor, token #…"
              className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
        )}
      </div>

      {!loading && <TablePager paging={paging} noun="receipt" className="px-4" rounded={false} />}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
            <SortHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={handleSort} />
            <SortHeader label="Token" sortKey="token" sortConfig={sortConfig} onSort={handleSort} />
            <SortHeader label="Patient" sortKey="patient" sortConfig={sortConfig} onSort={handleSort} />
            <SortHeader label="Doctor" sortKey="doctor" sortConfig={sortConfig} onSort={handleSort} />
            <SortHeader label="Service" sortKey="service" sortConfig={sortConfig} onSort={handleSort} />
            <SortHeader label="Fee" sortKey="fee" sortConfig={sortConfig} onSort={handleSort} align="right" />
            <SortHeader label="Mode" sortKey="mode" sortConfig={sortConfig} onSort={handleSort} />
            <SortHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            <tr><td colSpan="8" className="py-12 text-center text-gray-500"><RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan="8" className="py-12 text-center text-gray-500">
              No receipts match the current filter.
            </td></tr>
          ) : paging.pageRows.map(v => {
            const cancelled = v.status === 'CANCELLED';
            const patientName = `${v.patient?.firstName || ''} ${v.patient?.lastName || ''}`.trim() || '—';
            const doctorName = v.doctor?.fullName || `${v.doctor?.firstName || ''} ${v.doctor?.lastName || ''}`.trim() || '—';
            const issuedAt = receiptIssuedAt(v);
            return (
              <tr
                key={v.id}
                tabIndex={0}
                onClick={() => setSelected(v)}
                onKeyDown={onActivateKey(() => setSelected(v))}
                className={`cursor-pointer hover:bg-emerald-50/60 ${cancelled ? 'opacity-60' : ''} ${ROW_FOCUS_RING}`}
                title="View receipt details"
              >
                <td className="px-4 py-2 text-gray-600">
                  <div>{formatClinicDate(v.visitDate, { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                  {issuedAt && <div className="text-xs text-gray-400">{formatClinicTime(issuedAt, {}, '')}</div>}
                </td>
                <td className="px-4 py-2 font-mono text-gray-900">{formatTokenDisplay(v)}</td>
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-900">{patientName}</div>
                  <div className="text-xs text-gray-500">{v.patient?.mrn}</div>
                </td>
                <td className="px-4 py-2 text-gray-700">{doctorName}</td>
                <td className="px-4 py-2 text-gray-700">{v.medicalService?.name || '—'}</td>
                <td className="px-4 py-2 text-right">
                  <div className={`font-semibold ${Number(v.refundedAmount || 0) > 0 ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    PKR {Number(v.finalFee || 0).toLocaleString()}
                  </div>
                  {Number(v.discountAmount || 0) > 0 && (
                    <div className="text-xs text-emerald-600">- PKR {Number(v.discountAmount).toLocaleString()}</div>
                  )}
                  {Number(v.appliedCreditAmount || 0) > 0 && (
                    <div className="text-xs text-violet-600">credit - PKR {Number(v.appliedCreditAmount).toLocaleString()}</div>
                  )}
                  {Number(v.refundedAmount || 0) > 0 && (
                    <div className="text-xs font-semibold text-rose-600">refunded PKR {Number(v.refundedAmount).toLocaleString()}</div>
                  )}
                </td>
                <td className="px-4 py-2">
                  {v.paymentMode ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      v.paymentMode === 'NONE'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-emerald-50 text-emerald-700'
                    }`}>
                      {PAYMENT_MODE_LABEL[v.paymentMode] || v.paymentMode}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400 italic">unrecorded</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    cancelled
                      ? 'bg-gray-100 text-gray-600'
                      : v.status === 'COMPLETED'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-blue-50 text-blue-700'
                  }`}>
                    {v.status?.replace(/_/g, ' ').toLowerCase() || '—'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!loading && <TablePager paging={paging} noun="receipt" position="bottom" className="px-4" />}

      {selected && (
        <ReceiptDetailModal visit={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

// ============================================================================
// OPD Receipt detail modal — full breakdown of a single consultation receipt
// (patient, visit, fee, payment, vitals, notes). Opened by clicking a row.
// ============================================================================
const money = (x) => `₨${Number(x || 0).toLocaleString()}`;

const ageFromDob = (dob) => {
  if (!dob) return null;
  const m = String(dob).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const birth = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const md = now.getMonth() - birth.getMonth();
  if (md < 0 || (md === 0 && now.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : null;
};

// Tinted category card — mirrors UserInfoCard / PatientDetailsModal so this modal feels like
// the rest of the product (gradient tint, serif title, icon chip).
const ReceiptCard = ({ title, icon: Icon, gradient, border, iconBg, children }) => (
  <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-5 border ${border} shadow-sm`}>
    <div className="flex items-center space-x-3 mb-4">
      <div className={`w-10 h-10 bg-gradient-to-br ${iconBg} rounded-xl flex items-center justify-center`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <h3 className="text-xl font-bold text-gray-800">
        {title}
      </h3>
    </div>
    <div className="space-y-2.5">{children}</div>
  </div>
);

// Label/value row — white-tinted pill, icon + label left, value right. Empty values show a
// muted placeholder so cards keep a consistent shape.
const ReceiptRow = ({ icon, label, value, highlight = false, mono = false }) => {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className={`flex items-center justify-between gap-4 px-3.5 py-2.5 rounded-xl ${
      highlight ? 'bg-white bg-opacity-80 shadow-sm' : 'bg-white bg-opacity-40'
    }`}>
      <div className="flex items-center gap-2.5 min-w-0">
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{label}</span>
      </div>
      <span className={`text-sm text-right break-words ${mono ? 'font-mono' : ''} ${
        isEmpty ? 'text-gray-400 italic font-normal' : 'font-semibold text-gray-900'
      }`}>
        {isEmpty ? '—' : value}
      </span>
    </div>
  );
};

const ReceiptDetailModal = ({ visit, onClose }) => {
  const receiptDialogRef = useDialog(onClose, { confirmDiscard: false });
  const v = visit || {};
  const p = v.patient || {};
  const cancelled = v.status === 'CANCELLED';
  const patientName = `${p.firstName || ''} ${p.lastName || ''}`.trim() || '—';
  const doctorName =
    v.doctor?.fullName || `${v.doctor?.firstName || ''} ${v.doctor?.lastName || ''}`.trim() || '—';
  const issuedAt = receiptIssuedAt(v);
  const age = ageFromDob(p.dateOfBirth);
  const discount = Number(v.discountAmount || 0);
  const issuedBy = v.createdBy
    ? v.createdBy.fullName || `${v.createdBy.firstName || ''} ${v.createdBy.lastName || ''}`.trim()
    : null;

  const vitals = [
    v.bpSystolic && v.bpDiastolic ? { label: 'Blood Pressure', value: `${v.bpSystolic}/${v.bpDiastolic} mmHg` } : null,
    v.pulse ? { label: 'Pulse', value: `${v.pulse} bpm` } : null,
    v.temperature ? { label: 'Temperature', value: `${v.temperature} °F` } : null,
    v.spo2 ? { label: 'SpO₂', value: `${v.spo2}%` } : null,
    v.respiratoryRate ? { label: 'Respiratory Rate', value: `${v.respiratoryRate} /min` } : null,
    v.bloodSugar ? { label: 'Blood Sugar', value: `${v.bloodSugar} mg/dL` } : null,
    v.weight ? { label: 'Weight', value: `${v.weight} kg` } : null,
    v.height ? { label: 'Height', value: `${v.height} cm` } : null,
    v.lengthCm ? { label: 'Length', value: `${v.lengthCm} cm` } : null,
    v.headCircumferenceCm ? { label: 'Head Circumference', value: `${v.headCircumferenceCm} cm` } : null,
    v.muacCm ? { label: 'MUAC', value: `${v.muacCm} cm` } : null,
  ].filter(Boolean);

  return (
    <div
      ref={receiptDialogRef}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Sticky emerald header — matches the User/Patient detail modals */}
        <div className="flex-shrink-0 relative bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 text-white overflow-hidden">
          <button aria-label="Close" onClick={onClose} className="absolute top-4 right-4 z-10 p-2 hover:bg-white/20 rounded-xl transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="relative p-6 flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center border-2 border-white border-opacity-30 flex-shrink-0">
              <Receipt className="h-8 w-8 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-3xl font-bold text-white">
                OPD Receipt
              </h1>
              <p className="text-emerald-50 text-sm mt-0.5">
                Token #{formatVisitToken(v)} · {formatClinicDateTime(issuedAt, { withZone: true }, '—')}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white">
                  {v.status?.replace(/_/g, ' ').toLowerCase() || '—'}
                </span>
                {v.isEmergency && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-500/80 text-white">
                    <AlertTriangle className="h-3 w-3" /> Emergency
                  </span>
                )}
                {v.isRevisit && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-400/90 text-amber-900">
                    Revisit
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-gray-50/40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ReceiptCard
              title="Patient"
              icon={User}
              gradient="from-blue-50 to-indigo-50"
              border="border-blue-100"
              iconBg="from-blue-500 to-indigo-600"
            >
              <ReceiptRow icon={<User className="h-4 w-4 text-blue-600" />} label="Name" value={patientName} highlight />
              <ReceiptRow icon={<User className="h-4 w-4 text-emerald-600" />} label="MRN" value={p.mrn} mono />
              <ReceiptRow icon={<Phone className="h-4 w-4 text-blue-600" />} label="Phone" value={p.phoneNumber || p.phone} />
              <ReceiptRow icon={<Shield className="h-4 w-4 text-amber-600" />} label="CNIC" value={p.cnic} />
              <ReceiptRow icon={<User className="h-4 w-4 text-purple-600" />} label="Gender" value={p.gender} />
              <ReceiptRow icon={<Calendar className="h-4 w-4 text-purple-600" />} label="Age" value={age != null ? `${age} yrs` : null} />
            </ReceiptCard>

            <ReceiptCard
              title="Visit"
              icon={Stethoscope}
              gradient="from-emerald-50 to-teal-50"
              border="border-emerald-100"
              iconBg="from-emerald-500 to-teal-600"
            >
              <ReceiptRow icon={<Clock className="h-4 w-4 text-emerald-600" />} label="Date & time" value={formatClinicDateTime(issuedAt, { withZone: true }, '—')} highlight />
              <ReceiptRow icon={<Calendar className="h-4 w-4 text-emerald-600" />} label="Type" value={v.visitTitle} />
              <ReceiptRow icon={<Stethoscope className="h-4 w-4 text-emerald-600" />} label="Doctor" value={doctorName} />
              <ReceiptRow icon={<Activity className="h-4 w-4 text-emerald-600" />} label="Service" value={v.medicalService?.name} />
              {v.isEmergency && <ReceiptRow icon={<AlertTriangle className="h-4 w-4 text-red-600" />} label="Emergency reason" value={v.emergencyReason} />}
              {v.isRevisit && <ReceiptRow icon={<AlertTriangle className="h-4 w-4 text-amber-600" />} label="Revisit reason" value={v.revisitReason} />}
            </ReceiptCard>
          </div>

          <ReceiptCard
            title="Fee & Payment"
            icon={Receipt}
            gradient="from-amber-50 to-orange-50"
            border="border-amber-100"
            iconBg="from-amber-500 to-orange-600"
          >
            <ReceiptRow label="Original fee" value={money(v.originalFee)} />
            {discount > 0 && <ReceiptRow label="Discount" value={<span className="text-emerald-600">- {money(discount)}</span>} />}
            <ReceiptRow label="Final fee" value={<span className="text-emerald-700 font-bold">{money(v.finalFee)}</span>} highlight />
            <ReceiptRow label="Payment mode" value={v.paymentMode ? (PAYMENT_MODE_LABEL[v.paymentMode] || v.paymentMode) : 'Unrecorded'} />
          </ReceiptCard>

          {vitals.length > 0 && (
            <ReceiptCard
              title="Vitals"
              icon={Heart}
              gradient="from-rose-50 to-pink-50"
              border="border-rose-100"
              iconBg="from-rose-500 to-pink-600"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {vitals.map((vt) => <ReceiptRow key={vt.label} label={vt.label} value={vt.value} />)}
              </div>
              {(v.vitalsRecordedBy || v.vitalsRecordedAt) && (
                <p className="text-xs text-gray-500 pt-1">
                  Recorded
                  {v.vitalsRecordedBy ? ` by ${v.vitalsRecordedBy.fullName || `${v.vitalsRecordedBy.firstName || ''} ${v.vitalsRecordedBy.lastName || ''}`.trim()}` : ''}
                  {v.vitalsRecordedAt ? ` · ${formatClinicDateTime(v.vitalsRecordedAt, {}, '')}` : ''}
                </p>
              )}
            </ReceiptCard>
          )}

          {v.notes && (
            <ReceiptCard
              title="Notes"
              icon={Activity}
              gradient="from-gray-50 to-slate-50"
              border="border-gray-200"
              iconBg="from-gray-500 to-gray-600"
            >
              <p className="text-sm text-gray-700 whitespace-pre-wrap px-1">{v.notes}</p>
            </ReceiptCard>
          )}

          {(issuedBy || issuedAt) && (
            <p className="text-xs text-gray-400 text-center">
              Issued{issuedBy ? ` by ${issuedBy}` : ''}
              {issuedAt ? ` · ${formatClinicDateTime(issuedAt, { withZone: true }, '')}` : ''}
            </p>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-3 border-t border-gray-100 bg-white flex justify-end">
          <button onClick={onClose} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenManagement;